import { ReactElement } from 'react'
import { Stack } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { Html } from '@react-three/drei'
import useAIChatFindingsStore, { type SceneFinding } from 'src/state/stores/aiChatFindings'
import FindingBadge from './FindingBadge'
import FindingPopup from './FindingPopup'
import { MarkerGroup, SEVERITY_LABELS, findingsTitle, openMarkerAfterDismissal } from './utils'

interface Props {
  projectId: string
  group: MarkerGroup
}

/**
 * The "!" of the findings on one spot; a click opens them next to it (and outlines the elements,
 * `AIChatSceneBridge`), a second click or the x closes them. One is open at a time. A dismissed
 * finding leaves this "!" (other elements keep it); the "!" stays open with the others, or goes
 * once none are left.
 */
const FindingMarker = ({ projectId, group }: Props): ReactElement => {
  // The drei <Html> portal renders outside the r3f tree, so the app's MUI theme context doesn't
  // reach it -- re-provide it from the theme available here.
  const theme = useTheme()
  const isOpen = useAIChatFindingsStore(
    state => state.openMarkerByProject[projectId]?.key === group.key,
  )
  const setOpenMarker = useAIChatFindingsStore(state => state.setOpenMarker)
  const open = () =>
    setOpenMarker(projectId, {
      key: group.key,
      elementGuids: group.elementGuids,
      severity: group.severity,
    })
  const close = () => setOpenMarker(projectId, null)
  const dismissFinding = useAIChatFindingsStore(state => state.dismissFinding)
  const dismiss = (finding: SceneFinding) =>
    dismissFinding(
      projectId,
      finding,
      group.elementGuids.filter(guid => finding.elementGuids.includes(guid)),
      openMarkerAfterDismissal(group, finding),
    )
  const { findings, position, severity } = group
  const tooltip =
    findings.length === 1
      ? `${SEVERITY_LABELS[severity]}: ${findings[0].title}`
      : `${findingsTitle(findings.length)}, schwerster: ${SEVERITY_LABELS[severity]}`

  return (
    <Html
      // drei applies a new zIndexRange only once the marker moves on screen, so the open popup
      // would stay behind the other "!" until the camera moves -- remount to apply it at once.
      key={isOpen ? 'open' : 'closed'}
      position={[position.x, position.y, position.z]}
      center
      zIndexRange={isOpen ? [101, 100] : [99, 0]}
      style={{ pointerEvents: 'auto' }}
    >
      <ThemeProvider theme={theme}>
        <Stack
          alignItems="center"
          // Clicks here are the marker's, not the scene's (no element selection behind it).
          onPointerDown={event => event.stopPropagation()}
          sx={{ position: 'relative' }}
        >
          <FindingBadge
            severity={severity}
            count={findings.length}
            isOpen={isOpen}
            tooltip={tooltip}
            onClick={isOpen ? close : open}
          />
          {isOpen && <FindingPopup findings={findings} onClose={close} onDismiss={dismiss} />}
        </Stack>
      </ThemeProvider>
    </Html>
  )
}

export default FindingMarker
